/**
 * Recommendations Page
 * 
 * Displays personalized music recommendations based on user's Music Profile
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { recommendationService } from '../services/RecommendationService';
import { spotifyAuth } from '../services/spotify';
import { spotifyAPI } from '../services/spotify';
import { youtubeAuth } from '../services/youtube';
import { youtubeAPI } from '../services/youtube';
import { db } from '../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import SpotifyPlayer from '../components/SpotifyPlayer';
import './Recommendations.css';

function Recommendations({ user }) {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRec, setSelectedRec] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [youtubeConnected, setYoutubeConnected] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [shuffle, setShuffle] = useState(false);
  const [playedIndices, setPlayedIndices] = useState([]); // Track played songs in shuffle mode
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'spotify', 'youtube', 'jookbox'
  const youtubePlayerRef = useRef(null);
  const jookboxAudioRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    loadRecommendations();
    setSpotifyConnected(spotifyAuth.isAuthenticated());
    setYoutubeConnected(youtubeAuth.isAuthenticated());
  }, [user, navigate]);

  const loadRecommendations = async () => {
    setLoading(true);
    setError(null);

    try {
      const recs = await recommendationService.generateRecommendations(user.uid);
      
      if (recs.error) {
        setError(recs.error);
      } else {
        setRecommendations(recs);
      }
    } catch (err) {
      console.error('Error loading recommendations:', err);
      setError('Failed to load recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadTracksForRecommendation = async (rec) => {
    setSelectedRec(rec);
    setLoadingTracks(true);
    setTracks([]);
    setCurrentTrack(null);
    setCurrentTrackIndex(0);
    setPlayedIndices([]);

    console.log('🎵 Loading tracks for:', rec.title);
    console.log('📋 Criteria:', rec.criteria);

    // Check connections
    const isSpotifyConnected = spotifyAuth.isAuthenticated();
    const isYoutubeConnected = youtubeAuth.isAuthenticated();
    setSpotifyConnected(isSpotifyConnected);
    setYoutubeConnected(isYoutubeConnected);

    const allTracks = [];

    try {
      // === SPOTIFY SEARCH ===
      if (isSpotifyConnected) {
        console.log('🟢 Searching Spotify...');
        
        // Search by artists
        if (rec.criteria.artists?.length > 0) {
          for (const artist of rec.criteria.artists.slice(0, 3)) {
            try {
              const results = await spotifyAPI.search(artist, ['track'], 3);
              if (results.tracks?.length > 0) {
                allTracks.push(...results.tracks.map(t => ({ ...t, source: 'spotify' })));
              }
            } catch (e) { console.warn('Spotify artist search failed:', artist); }
          }
        }
        
        // Search by genres
        if (rec.criteria.genres?.length > 0) {
          for (const genre of rec.criteria.genres.slice(0, 2)) {
            try {
              const results = await spotifyAPI.search(`${genre} music`, ['track'], 3);
              if (results.tracks?.length > 0) {
                allTracks.push(...results.tracks.map(t => ({ ...t, source: 'spotify' })));
              }
            } catch (e) { console.warn('Spotify genre search failed:', genre); }
          }
        }
      }

      // === YOUTUBE SEARCH ===
      if (isYoutubeConnected) {
        console.log('🔴 Searching YouTube...');
        
        // Search by artists
        if (rec.criteria.artists?.length > 0) {
          for (const artist of rec.criteria.artists.slice(0, 3)) {
            try {
              const results = await youtubeAPI.search(`${artist} official audio`, null, 3);
              if (results.items?.length > 0) {
                allTracks.push(...results.items.map(t => ({
                  id: t.id,
                  name: t.title,
                  artist: t.artist,
                  image: t.thumbnail,
                  source: 'youtube',
                  videoId: t.id
                })));
              }
            } catch (e) { console.warn('YouTube artist search failed:', artist); }
          }
        }
        
        // Search by genres
        if (rec.criteria.genres?.length > 0) {
          for (const genre of rec.criteria.genres.slice(0, 2)) {
            try {
              const results = await youtubeAPI.search(`${genre} music mix`, null, 3);
              if (results.items?.length > 0) {
                allTracks.push(...results.items.map(t => ({
                  id: t.id,
                  name: t.title,
                  artist: t.artist,
                  image: t.thumbnail,
                  source: 'youtube',
                  videoId: t.id
                })));
              }
            } catch (e) { console.warn('YouTube genre search failed:', genre); }
          }
        }
      }

      // === JOOKBOX LIBRARY SEARCH ===
      console.log('🎵 Searching Jookbox Library...');
      try {
        // Get all available albums
        const albumsQuery = query(
          collection(db, 'albums-flat'),
          where('storageStatus', '!=', 'unavailable')
        );
        const albumsSnap = await getDocs(albumsQuery);
        
        // Filter by criteria
        albumsSnap.forEach((docSnap) => {
          const album = { id: docSnap.id, ...docSnap.data() };
          
          // Check if album matches criteria
          const matchesArtist = rec.criteria.artists?.some(a => 
            album.artist?.toLowerCase().includes(a.toLowerCase())
          );
          const matchesGenre = rec.criteria.genres?.some(g => 
            album.genre?.toLowerCase().includes(g.toLowerCase())
          );
          
          if (matchesArtist || matchesGenre) {
            // Add songs from matching album
            if (album.songs?.length > 0) {
              const songsToAdd = album.songs.slice(0, 2).map(song => ({
                id: `jookbox-${album.id}-${song.title}`,
                name: song.title || song.fileName?.replace('.mp3', ''),
                artist: album.artist,
                album: album.album,
                image: album.coverArt || '/default-album.png',
                source: 'jookbox',
                audioUrl: song.url || song.storageUrl,
                duration: song.duration
              }));
              allTracks.push(...songsToAdd);
            }
          }
        });
        console.log('  Found Jookbox tracks:', allTracks.filter(t => t.source === 'jookbox').length);
      } catch (e) {
        console.warn('Jookbox search failed:', e);
      }

      // Remove duplicates by name+artist and shuffle sources together
      const uniqueTracks = allTracks.filter((track, index, self) =>
        index === self.findIndex(t => 
          t.name?.toLowerCase() === track.name?.toLowerCase() && 
          t.artist?.toLowerCase() === track.artist?.toLowerCase()
        )
      );

      // Sort to interleave sources (mix it up!)
      const shuffledTracks = uniqueTracks.sort(() => Math.random() - 0.5).slice(0, 30);

      setTracks(shuffledTracks);
      console.log(`✅ Loaded ${shuffledTracks.length} tracks from all sources`);
      
      // Auto-play first track
      if (shuffledTracks.length > 0) {
        const startIndex = shuffle ? Math.floor(Math.random() * shuffledTracks.length) : 0;
        setCurrentTrackIndex(startIndex);
        setCurrentTrack(shuffledTracks[startIndex]);
        setPlayedIndices([startIndex]);
        console.log('▶️ Auto-playing:', shuffledTracks[startIndex].name, `(${shuffledTracks[startIndex].source})`);
      }
    } catch (err) {
      console.error('Error loading tracks:', err);
    } finally {
      setLoadingTracks(false);
    }
  };

  const playTrack = (track, index) => {
    if (!spotifyAuth.isAuthenticated()) {
      alert('Connect Spotify to play tracks!');
      return;
    }

    setCurrentTrack(track);
    setCurrentTrackIndex(index);
    if (shuffle) {
      setPlayedIndices(prev => [...prev, index]);
    }
    console.log('🎵 Playing:', track.name, 'by', track.artist);
  };

  const getRandomUnplayedIndex = () => {
    // Get indices that haven't been played yet
    const unplayed = tracks.map((_, i) => i).filter(i => !playedIndices.includes(i));
    if (unplayed.length === 0) {
      // All tracks played, reset
      setPlayedIndices([]);
      return Math.floor(Math.random() * tracks.length);
    }
    return unplayed[Math.floor(Math.random() * unplayed.length)];
  };

  const handleNextTrack = () => {
    if (tracks.length === 0) return;

    let nextIndex;
    if (shuffle) {
      nextIndex = getRandomUnplayedIndex();
      setPlayedIndices(prev => [...prev, nextIndex]);
    } else {
      nextIndex = currentTrackIndex < tracks.length - 1 ? currentTrackIndex + 1 : 0;
    }
    
    setCurrentTrackIndex(nextIndex);
    setCurrentTrack(tracks[nextIndex]);
  };

  const handlePreviousTrack = () => {
    if (tracks.length === 0) return;

    let prevIndex;
    if (shuffle && playedIndices.length > 1) {
      // Go back in shuffle history
      const newHistory = [...playedIndices];
      newHistory.pop(); // Remove current
      const lastPlayed = newHistory[newHistory.length - 1];
      setPlayedIndices(newHistory);
      prevIndex = lastPlayed;
    } else {
      prevIndex = currentTrackIndex > 0 ? currentTrackIndex - 1 : tracks.length - 1;
    }
    
    setCurrentTrackIndex(prevIndex);
    setCurrentTrack(tracks[prevIndex]);
  };

  const handleTrackEnd = () => {
    if (autoplay) {
      handleNextTrack();
    }
  };

  const toggleAutoplay = () => setAutoplay(!autoplay);
  const toggleShuffle = () => {
    setShuffle(!shuffle);
    setPlayedIndices(shuffle ? [] : [currentTrackIndex]); // Reset history when toggling
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="recommendations-page">
        <div className="recommendations-loading">
          <div className="loading-spinner">🎵</div>
          <p>Analyzing your music profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recommendations-page">
        <div className="recommendations-error">
          <div className="error-icon">📋</div>
          <h2>Complete Your Profile First</h2>
          <p>{error}</p>
          <Link to="/music-profile" className="profile-link-btn">
            🎵 Go to Music Profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="recommendations-page">
      <div className="recommendations-container">
        <header className="recommendations-header">
          <h1>🎵 Your Personalized Music</h1>
          <p>Playlists crafted from your Music Profile</p>
          <Link to="/services" className="back-link">← Back to Services</Link>
        </header>

        {recommendations.length === 0 ? (
          <div className="no-recommendations">
            <p>Add more details to your Music Profile to unlock personalized recommendations!</p>
            <Link to="/music-profile" className="profile-link-btn">
              Complete Profile
            </Link>
          </div>
        ) : (
          <div className="recommendations-grid">
            {recommendations.map(rec => (
              <div
                key={rec.id}
                className={`recommendation-card ${selectedRec?.id === rec.id ? 'selected' : ''}`}
                style={{ '--card-color': rec.color }}
                onClick={() => loadTracksForRecommendation(rec)}
              >
                <div className="rec-icon">{rec.icon}</div>
                <h3 className="rec-title">{rec.title}</h3>
                <p className="rec-subtitle">{rec.subtitle}</p>
                <div className="rec-type">{rec.type}</div>
              </div>
            ))}
          </div>
        )}

        {/* Track Preview Panel */}
        {selectedRec && (
          <div className="tracks-panel">
            <div className="tracks-header">
              <h2>{selectedRec.icon} {selectedRec.title}</h2>
              <div className="playback-controls">
                <button 
                  className={`control-btn ${shuffle ? 'active' : ''}`}
                  onClick={toggleShuffle}
                  title={shuffle ? 'Shuffle On' : 'Shuffle Off'}
                >
                  🔀
                </button>
                <button 
                  className={`control-btn ${autoplay ? 'active' : ''}`}
                  onClick={toggleAutoplay}
                  title={autoplay ? 'Autoplay On' : 'Autoplay Off'}
                >
                  🔁
                </button>
                <button className="close-panel" onClick={() => setSelectedRec(null)}>×</button>
              </div>
            </div>
            
            {/* Source Filter */}
            <div className="source-filters">
              <button 
                className={`source-btn ${sourceFilter === 'all' ? 'active' : ''}`}
                onClick={() => setSourceFilter('all')}
              >
                All
              </button>
              <button 
                className={`source-btn spotify ${sourceFilter === 'spotify' ? 'active' : ''}`}
                onClick={() => setSourceFilter('spotify')}
                disabled={!spotifyConnected}
              >
                🟢 Spotify
              </button>
              <button 
                className={`source-btn youtube ${sourceFilter === 'youtube' ? 'active' : ''}`}
                onClick={() => setSourceFilter('youtube')}
                disabled={!youtubeConnected}
              >
                🔴 YouTube
              </button>
              <button 
                className={`source-btn jookbox ${sourceFilter === 'jookbox' ? 'active' : ''}`}
                onClick={() => setSourceFilter('jookbox')}
              >
                🎵 Jookbox
              </button>
            </div>

            {!spotifyConnected ? (
              <div className="connect-prompt">
                <p>Connect Spotify to see tracks for this playlist</p>
                <Link to="/services" className="connect-btn">Connect Spotify</Link>
              </div>
            ) : loadingTracks ? (
              <div className="tracks-loading">
                <div className="loading-dots">
                  <span>.</span><span>.</span><span>.</span>
                </div>
                <p>Finding tracks...</p>
              </div>
            ) : tracks.length === 0 ? (
              <div className="no-tracks">
                <p>No tracks found. Try connecting more services or updating your profile.</p>
              </div>
            ) : (
              <div className="tracks-list">
                {tracks
                  .filter(t => sourceFilter === 'all' || t.source === sourceFilter)
                  .map((track, index) => (
                  <div 
                    key={track.id} 
                    className={`track-item ${currentTrack?.id === track.id ? 'playing' : ''} source-${track.source}`} 
                    onClick={() => playTrack(track, tracks.indexOf(track))}
                  >
                    <span className="source-icon">
                      {track.source === 'spotify' && '🟢'}
                      {track.source === 'youtube' && '🔴'}
                      {track.source === 'jookbox' && '🎵'}
                    </span>
                    <img
                      src={track.image || '/default-album.png'}
                      alt={track.album || track.name}
                      className="track-image"
                    />
                    <div className="track-info">
                      <span className="track-name">{track.name}</span>
                      <span className="track-artist">{track.artist}</span>
                    </div>
                    <span className="play-icon">
                      {currentTrack?.id === track.id ? '🎵' : '▶'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Multi-Source Player - Fixed at bottom */}
        {currentTrack && (
          <div className="player-container">
            {/* Spotify Player */}
            {currentTrack.source === 'spotify' && (
              <SpotifyPlayer
                trackUri={currentTrack.uri}
                currentTrack={{
                  name: currentTrack.name,
                  artist: currentTrack.artist,
                  image: currentTrack.image
                }}
                onTrackEnd={handleTrackEnd}
                onNext={handleNextTrack}
                onPrevious={handlePreviousTrack}
                queueLength={tracks.length}
                currentIndex={currentTrackIndex}
              />
            )}
            
            {/* YouTube Player */}
            {currentTrack.source === 'youtube' && (
              <div className="youtube-player-wrapper">
                <div className="youtube-now-playing">
                  <img src={currentTrack.image} alt="" className="now-playing-image" />
                  <div className="now-playing-info">
                    <span className="now-playing-name">{currentTrack.name}</span>
                    <span className="now-playing-artist">🔴 YouTube • {currentTrack.artist}</span>
                  </div>
                  <div className="player-controls">
                    <button onClick={handlePreviousTrack}>⏮️</button>
                    <button onClick={handleNextTrack}>⏭️</button>
                  </div>
                </div>
                <iframe
                  ref={youtubePlayerRef}
                  width="100%"
                  height="80"
                  src={`https://www.youtube.com/embed/${currentTrack.videoId}?autoplay=1&enablejsapi=1`}
                  title={currentTrack.name}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ borderRadius: '8px' }}
                />
              </div>
            )}
            
            {/* Jookbox Audio Player */}
            {currentTrack.source === 'jookbox' && (
              <div className="jookbox-player-wrapper">
                <div className="jookbox-now-playing">
                  <img src={currentTrack.image} alt="" className="now-playing-image" />
                  <div className="now-playing-info">
                    <span className="now-playing-name">{currentTrack.name}</span>
                    <span className="now-playing-artist">🎵 Jookbox • {currentTrack.artist}</span>
                  </div>
                  <div className="player-controls">
                    <button onClick={handlePreviousTrack}>⏮️</button>
                    <button onClick={handleNextTrack}>⏭️</button>
                  </div>
                </div>
                <audio
                  ref={jookboxAudioRef}
                  src={currentTrack.audioUrl}
                  autoPlay
                  controls
                  onEnded={handleTrackEnd}
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </div>
        )}

        {/* Profile Summary */}
        <div className="profile-summary-section">
          <h3>📊 Your Music DNA</h3>
          <div className="dna-items">
            {recommendationService.profile?.languages?.length > 0 && (
              <div className="dna-item">
                <span className="dna-label">Languages</span>
                <span className="dna-value">{recommendationService.profile.languages.join(', ')}</span>
              </div>
            )}
            {recommendationService.profile?.heritage?.length > 0 && (
              <div className="dna-item">
                <span className="dna-label">Heritage</span>
                <span className="dna-value">{recommendationService.profile.heritage.join(', ')}</span>
              </div>
            )}
            {recommendationService.profile?.favoriteGenres?.length > 0 && (
              <div className="dna-item">
                <span className="dna-label">Your Genres</span>
                <span className="dna-value">{recommendationService.profile.favoriteGenres.slice(0, 4).join(', ')}</span>
              </div>
            )}
          </div>
          <Link to="/music-profile" className="edit-profile-link">
            ✏️ Edit Profile
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Recommendations;

